/**
 * A move of a list of numbers from where they were shown to where they are now, over `duration`
 * milliseconds, eased out. A missing number (`null`) is not moved: it, and a number that appears
 * where there was none, is shown at once.
 */
export interface Tween {
    readonly from: readonly (number | null)[];
    readonly to: readonly (number | null)[];
    /** When the move started, on the clock `now` is given in. */
    readonly start: number;
    readonly duration: number;
}

/** Fast at first, slowing to a stop (cubic). */
export const easeOut = (t: number) : number => 1 - (1 - t) ** 3;

/** A tween that is already there: `values` shown as they are. */
export function stillTween(values: readonly (number | null)[]) : Tween {
    return { from: values, to: values, start: 0, duration: 0 };
}

/** The numbers to show at `now`. */
export function tweenAt(tween: Tween, now: number) : (number | null)[] {
    const t = tween.duration > 0 ? Math.min(1, Math.max(0, (now - tween.start) / tween.duration)) : 1;
    if (t >= 1) {
        return [...tween.to];
    }

    const eased = easeOut(t);

    return tween.to.map((to, index) => {
        const from = tween.from[index];
        return to === null || from === null || from === undefined ? to : from + (to - from) * eased;
    });
}

/** Whether the tween is still moving at `now`. */
export function isTweening(tween: Tween, now: number) : boolean {
    return tween.duration > 0 && now < tween.start + tween.duration && tween.to.some((to, index) => {
        const from = tween.from[index];
        return to !== null && from !== null && from !== undefined && from !== to;
    });
}

/**
 * The tween to `to` from what `tween` shows at `now`, so that a new value in the middle of a move
 * carries on from where the numbers are instead of jumping back. The same `to` keeps `tween`.
 */
export function retarget(tween: Tween, to: readonly (number | null)[], now: number, duration: number) : Tween {
    if (to.length === tween.to.length && to.every((value, index) => value === tween.to[index])) {
        return tween;
    }

    return { from: tweenAt(tween, now), to, start: now, duration };
}
